import { CellList } from "../backend/backendapi";
import { TreeElement } from "../osxtreeview/osxtreeview.model";

export class CellTree {
    item!: CellList;
    children: CellTree[] = [];

    flatMap() {
        return [this].concat(this.children.map(c => c.flatMap()).reduce((l, r) => l.concat(r), []));
    }
}

export abstract class ICellTreeCollapsable {
    abstract get item(): CellList;

    abstract get children(): ICellTreeCollapsable[];

    abstract get collapsed(): boolean;
    abstract set collapsed(val: boolean);

    flatMap() {
        return [this].concat(this.collapsed ? [] : this.children.map(c => c.flatMap()).reduce((l, r) => l.concat(r), []));
    }

    deepSorted(reverse: boolean): ICellTreeCollapsable {
        return new SortedCellTreeCollapsable(this, reverse);
    }
}

export class CellTreeCollapsable extends ICellTreeCollapsable {
    private readonly itemInternal!: CellList;
    private readonly childrenInternal: ICellTreeCollapsable[] = [];
    private collapsedInternal: boolean = false;

    get item(): CellList {
        return this.itemInternal;
    }

    get children(): ICellTreeCollapsable[] {
        return this.childrenInternal;
    }

    get collapsed(): boolean {
        return this.collapsedInternal;
    }

    set collapsed(val: boolean) {
        this.collapsedInternal = val;
    }

    constructor(celltree: CellTree) {
        super();
        this.itemInternal = celltree.item;
        this.childrenInternal = celltree.children.map(e => new CellTreeCollapsable(e));
    }
}

class SortedCellTreeCollapsable extends ICellTreeCollapsable {
    private readonly wrapped!: ICellTreeCollapsable;
    private readonly reverse!: boolean;

    get item(): CellList {
        return this.wrapped.item;
    }

    get children(): ICellTreeCollapsable[] {
        return this.wrapped.children
            .map(e => e.deepSorted(this.reverse))
            .sort((l, r) => (l.item.name ?? "").localeCompare(r.item.name ?? "") * (this.reverse ? -1 : 1));
    }

    get collapsed(): boolean {
        return this.wrapped.collapsed;
    }

    set collapsed(val: boolean) {
        this.wrapped.collapsed = val;
    }

    constructor(celltree: ICellTreeCollapsable, reverse: boolean) {
        super();
        this.wrapped = celltree;
        this.reverse = reverse;
    }
}

export class OsxTreeElement implements TreeElement<ICellTreeCollapsable, OsxTreeElement> {
    item: ICellTreeCollapsable;
    parent: OsxTreeElement | null;
    children: OsxTreeElement[] = [];

    get id(): number {
        return this.item.item.id!;
    }

    get html(): string {
        return this.item.item.name!;
    }
    
    get searchableText(): string {
        return [this.item.item.name, this.item.item.ident].filter(e => !!e).join(' ');
    }

    constructor(item: ICellTreeCollapsable, parent: OsxTreeElement | null) {
        this.item = item;
        this.parent = parent;
        this.children = item.children.map(e => new OsxTreeElement(e, this));
    }
}
